import React, { useState, useEffect } from "react";
import { getQuizsAll, addTabToDatabase } from "utils/firebaseFunction";
import { fetchTabsFromDatabase } from "utils/firebaseFunction";
import { GrLinkNext } from "react-icons/gr";
import { MainContext } from "utils/context";
import { useContext } from "react";
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

export default function QuizInfoForm({ quizInfo, handleQuizInfoChange, nextStep }) {
  const [tabs, setTabs] = useState([]);
  const [newTabName, setNewTabName] = useState("");
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [tabError, setTabError] = useState("");
  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(true);
  const { userId } = useContext(MainContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const fetchedTabs = await fetchTabsFromDatabase();
        setTabs(fetchedTabs);
      } catch (error) {
        setGeneralError("Failed to fetch data: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateTab = async () => {
    if (!newTabName.trim()) {
      setTabError("Tab name cannot be empty!");
      return;
    }

    if (tabs.includes(newTabName)) {
      setTabError("Tab already exists!");
      return;
    }

    try {
      setLoading(true);
      await addTabToDatabase(newTabName);
      setTabs([...tabs, newTabName]);
      handleQuizInfoChange({
        target: { name: "tab", value: newTabName },
      });
      setNewTabName("");
      setTabError("");
      setGeneralError("Tab created successfully!");
    } catch (error) {
      setTabError("Failed to create tab: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    let hasError = false;

    if (!quizInfo.title.trim()) {
      setTitleError("Quiz title is required.");
      hasError = true;
    } else if (quizInfo.title.length < 3) {
      setTitleError("Quiz title must be at least 3 characters long.");
      hasError = true;
    } else {
      setTitleError("");
    }

    if (!quizInfo.description.trim()) {
      setDescriptionError("Quiz description is required.");
      hasError = true;
    } else if (quizInfo.description.length < 10) {
      setDescriptionError("Quiz description must be at least 10 characters long.");
      hasError = true;
    } else {
      setDescriptionError("");
    }

    if (!quizInfo.tab) {
      setTabError("Please select a tab.");
      hasError = true;
    } else {
      setTabError("");
    }

    if (hasError) {
      return;
    }

    setGeneralError("");
    nextStep();
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Loading quiz information..." />;
  }

  return (
    <div className="quiz-info-form">
      <h3>Quiz Information</h3>
      
      <div className="form-group">
        <label>Quiz Title:</label>
        <input
          type="text"
          name="title"
          value={quizInfo.title}
          onChange={handleQuizInfoChange}
          placeholder="Enter a descriptive title"
          required
        />
        {titleError && <div className="error-message">{titleError}</div>}
      </div>

      <div className="form-group">
        <label>Quiz Description:</label>
        <textarea
          name="description"
          value={quizInfo.description}
          onChange={handleQuizInfoChange}
          placeholder="Describe what this quiz is about"
          required
          rows="4"
        />
        {descriptionError && <div className="error-message">{descriptionError}</div>}
      </div>

      <div className="form-group">
        <label>Select Tab:</label>
        <select
          name="tab"
          value={quizInfo.tab}
          onChange={handleQuizInfoChange}
          required
        >
          <option value="">Select a Tab</option>
          {tabs.map((tab, index) => (
            <option key={index} value={tab}>
              {tab}
            </option>
          ))}
        </select>
        {tabError && <div className="error-message">{tabError}</div>}
        
        {userId === "JuAxp52a7uMSF7vrzwxKVpIN6Nw2" && (
          <div className="create-tab">
            <input
              type="text"
              placeholder="New Tab Name"
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
            />
            <button 
              className="Btn" 
              type="button" 
              onClick={handleCreateTab}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Tab"}
            </button>
          </div>
        )}
      </div>

      {generalError && (
        <div className={`message ${generalError.includes("successfully") ? "success" : "error"}`}>
          {generalError}
        </div>
      )}

      <button 
        type="button" 
        className="submit-button Btn" 
        onClick={handleNextStep}
        disabled={loading}
      >
        Next Step <GrLinkNext />
      </button>
    </div>
  );
}